import { useMemo, useRef, useState } from 'react'
import { fingerprint, useToolProgress } from './progress'
import { useI18n } from '@/i18n/I18nProvider'
import {
  ROI_CFG,
  ROI_COUNTRIES,
  ROI_INDUSTRIES,
  ROI_OBJECTIVES,
  ROI_REGIONS,
} from '@/content/tools/roi-calculator'
import LeadGate from './LeadGate'
import type { SubmitState, ToolResult } from './submit'

/**
 * The Technology ROI Calculator. Ported from Halil's standalone HTML,
 * 2026-08-29 — see `content/tools/roi-calculator.ts` for what changed and why.
 *
 * This is the only one of the four tools that is not a questionnaire: four
 * inputs, an arithmetic model, and a result whose whole job is to be argued
 * with in a meeting. So it does not use the shared question chrome, and the
 * phases are `form → teaser → gate → report` rather than the others'
 * `intro → quiz → gate → report`.
 *
 * THE TEASER IS THE POINT OF THE SHAPE. The tangible number and the value ratio
 * are given away before the gate; the intangible, the avoided cost and the
 * Digitopia ROI sit behind it. That is Halil's design and it is the right way
 * round: something real has to be on the screen before an email is worth
 * asking for, and the free half is exactly the half a sceptic would accept
 * anyway.
 */

/** $18.5M / $250K / $900 — three significant figures is as precise as this model earns. */
function money(n: number): string {
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(n < 1e10 ? 2 : 1) + 'B'
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(n < 1e7 ? 2 : 1) + 'M'
  if (n >= 1e3) return '$' + Math.round(n / 1e3).toLocaleString('en-US') + 'K'
  return '$' + Math.round(n).toLocaleString('en-US')
}

/**
 * Alphabetical, with the "Other …" entries held at the bottom where they belong.
 *
 * `key` is the ENGLISH name, because that is what marks an "Other" row in every
 * locale; the sort itself uses the displayed name and the reader's own collation,
 * so the Turkish list is in Turkish alphabetical order rather than English one.
 */
function otherLast(locale: string) {
  return (a: { key: string; name: string }, b: { key: string; name: string }) => {
    const ao = a.key.startsWith('Other')
    const bo = b.key.startsWith('Other')
    if (ao !== bo) return ao ? 1 : -1
    return a.name.localeCompare(b.name, locale)
  }
}

/** `'… {x} …'` with the numbers and names put in. */
function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in values ? String(values[k]) : m))
}

/**
 * The result before there is one — the gate is not on screen yet, but the memo
 * has to return something of the right shape.
 *
 * A named constant rather than an object literal inside the `if`, because two
 * returns whose `dimensions` differ union into an object with six
 * optional-undefined keys and that no longer satisfies `Record<string, number>`.
 * Annotating it here types both branches the same.
 */
const NOT_YET: ToolResult = {
  score: null,
  verdict: '',
  band: '',
  dimensions: {},
  answers: {},
}

/**
 * Country and Industry, as a list of ours rather than a native `<select>`.
 *
 * Asked for on 2026-08-30: the browser's own popup for 200-odd countries opens
 * as a panel over most of the page, and no CSS of ours can reach its height.
 * This is the same shape the contact form settled on — ten rows at
 * `py-2 text-sm` is `max-h-[22.5rem]`, and everything past the tenth scrolls
 * inside the panel.
 *
 * The panel is **positioned, not in the flow**. It was in the flow first, and
 * that was wrong: opening Country pushed Industry, the objective chips and the
 * button down the page and grew the document, so the form visibly came apart
 * under the pointer. A form must not change shape when you touch it — the
 * panel floats above the fields on its own layer, so the composition behind it
 * is byte-for-byte where it was before the click. It closes on Escape and on a
 * click anywhere outside it.
 */
function Picker<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { value: T; name: string }[]
  onChange: (v: T) => void
}) {
  const [open, setOpen] = useState(false)
  const current = options.find((o) => o.value === value)

  return (
    <div
      className={'relative block ' + (open ? 'z-20' : '')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="mt-2 flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left text-base text-foreground focus:border-foreground focus:outline-none"
      >
        <span className="truncate">{current?.name ?? ''}</span>
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {/* Closes the panel on a click anywhere else, without a document listener
          — and, being fixed, it never adds to the page's own height. */}
      {open && (
        <div
          aria-hidden
          className="fixed inset-0 z-10 cursor-default"
          onClick={() => setOpen(false)}
        />
      )}
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-[22.5rem] overflow-y-auto rounded-md border border-border bg-card py-1 shadow-lg"
        >
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
              className={
                'flex w-full cursor-pointer items-center px-4 py-2 text-left text-sm hover:bg-sidebar-accent '
                + (o.value === value ? 'bg-sidebar-accent font-medium' : '')
              }
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RoiCalculator() {
  const { t, locale } = useI18n()
  const c = t.tools.roiCalculator

  /* The lists the two pickers show. Names come from the dictionary, the model
     stays keyed by English, and the order is the reader's own alphabet. */
  const industryOptions = useMemo(
    () =>
      Object.keys(ROI_INDUSTRIES)
        .map((key) => ({ key, name: c.industries[key]?.name ?? ROI_INDUSTRIES[key].name }))
        .sort(otherLast(locale)),
    [c, locale],
  )
  const countryOptions = useMemo(
    () =>
      ROI_COUNTRIES.map((ctr, i) => ({ key: ctr[0], name: c.countries[ctr[0]] ?? ctr[0], i }))
        .sort(otherLast(locale)),
    [c, locale],
  )

  const openedAt = useRef(Date.now()).current
  const [phase, setPhase] = useState<'form' | 'teaser' | 'gate' | 'report'>('form')
  const [saved, setSaved] = useState<SubmitState>({ status: 'idle' })

  const [revenue, setRevenue] = useState('')
  const [countryIndex, setCountryIndex] = useState(0)
  const [industryKey, setIndustryKey] = useState(Object.keys(ROI_INDUSTRIES)[0])
  const [objectives, setObjectives] = useState<string[]>([])
  const [error, setError] = useState('')

  /*
    THE FORM SURVIVES A REFRESH — in this browser only. See `tools/progress.ts`
    for why none of this reaches our database.

    NO RESUME OFFER HERE, and that is the difference from the three quizzes. This
    tool is one screen: restoring it puts the reader's revenue, country, industry
    and objectives back in fields they can SEE, so there is nothing to announce
    and no age to distinguish. A banner saying "continue where you left off"
    above a form that is visibly already filled in would be noise. The quizzes
    need the offer because their state is a question index the reader cannot see.

    Revenue is the reader's own text, so it is length-capped on the way back in:
    a stored value is editable by them, and there is no reason for this field to
    accept a novel.
  */
  const progress = useToolProgress({
    tool: 'roi-calculator',
    shape: fingerprint([
      Object.keys(ROI_INDUSTRIES),
      ROI_COUNTRIES.map((ctr) => ctr[0]),
      Object.keys(ROI_OBJECTIVES),
    ]),
    state: useMemo(
      () => ({ revenue, countryIndex, industryKey, objectives }),
      [revenue, countryIndex, industryKey, objectives],
    ),
    active: phase === 'form' || phase === 'teaser' || phase === 'gate',
    restore: (saved) => {
      // Untrusted — every value is checked against the live model's own keys,
      // never trusted as an index or a name.
      if (!saved || typeof saved !== 'object') return
      if (typeof saved.revenue === 'string') setRevenue(saved.revenue.slice(0, 32))
      if (
        typeof saved.countryIndex === 'number' &&
        Number.isInteger(saved.countryIndex) &&
        saved.countryIndex >= 0 &&
        saved.countryIndex < ROI_COUNTRIES.length
      ) {
        setCountryIndex(saved.countryIndex)
      }
      if (
        typeof saved.industryKey === 'string' &&
        Object.prototype.hasOwnProperty.call(ROI_INDUSTRIES, saved.industryKey)
      ) {
        setIndustryKey(saved.industryKey)
      }
      if (Array.isArray(saved.objectives)) {
        setObjectives(
          saved.objectives.filter(
            (o): o is string =>
              typeof o === 'string' &&
              Object.prototype.hasOwnProperty.call(ROI_OBJECTIVES, o),
          ),
        )
      }
    },
  })

  /** The chosen industry's words, which a locale may have rewritten. */
  const indCopy = c.industries[industryKey] ?? ROI_INDUSTRIES[industryKey]
  /** The chosen objectives as the reader sees them; the model still uses the keys. */
  const objectiveLabels = objectives.map((o) => c.objectives[o]?.label ?? o)

  /**
   * The model, exactly as the source computes it.
   *
   * Returns null until the inputs are usable, so the same expression answers
   * "can we calculate?" and "what is the answer?" — there is no separate
   * validity flag to keep in step with the numbers.
   */
  const calc = useMemo(() => {
    const rev = Number(revenue.replace(/[^0-9]/g, ''))
    if (!rev || rev < 1e6 || !objectives.length) return null

    const ctr = ROI_COUNTRIES[countryIndex]
    const ind = ROI_INDUSTRIES[industryKey]

    const regionFactor = ROI_REGIONS[ctr[1]] / ROI_CFG.REGION_MEDIAN
    const itBudget = rev * (ind.it / 100) * regionFactor
    const gt = itBudget * ((ind.grow + ind.tr) / 100)

    const mod = objectives.reduce((s, o) => s + ROI_OBJECTIVES[o].mod, 0) / objectives.length
    const tangible = gt * ROI_CFG.VALUE_MULTIPLE * mod
    const intangible = tangible * ROI_CFG.INTANGIBLE_RATIO
    const opportunity = (tangible + intangible) * ROI_CFG.OPPORTUNITY_RATIO
    const total = tangible + intangible + opportunity

    const cost = ctr[2] === 'developed' ? ROI_CFG.COST_DEVELOPED : ROI_CFG.COST_EMERGING

    return {
      rev, ctr, ind, itBudget, gt, tangible, intangible, opportunity, total,
      digiRoi: total / cost,
      /** Complete value returned per dollar of the WHOLE tech budget, not just Grow & Transform. */
      valueRatio: total / itBudget,
      costShare: (cost / itBudget) * 100,
    }
  }, [revenue, countryIndex, industryKey, objectives])

  /**
   * What the CRM gets.
   *
   * `score` is null on purpose. `ToolResult.score` is the 0–100 column the other
   * three tools sort on, and this tool has no comparable headline: a ratio is
   * not a percentage, and squeezing "4.2x" into that column would put it beside
   * maturity scores it has nothing to do with. The money is in `dimensions`,
   * where a number means whatever its label says.
   */
  // Typed on `useMemo`, not on the const: two returns whose `dimensions` differ
  // (`{}` before the inputs are usable, six money lines after) union into an
  // object with six optional-undefined keys, which no longer satisfies
  // `Record<string, number>`. The type argument makes both branches
  // contextually typed instead of inferred and then checked.
  const result = useMemo<ToolResult>(() => {
    if (!calc) return NOT_YET
    return {
      score: null,
      verdict: `${money(calc.total)} total annual value potential`,
      band: `${calc.valueRatio.toFixed(1)}x complete value ratio`,
      dimensions: {
        'IT budget': Math.round(calc.itBudget),
        'Grow & Transform budget': Math.round(calc.gt),
        Tangible: Math.round(calc.tangible),
        Intangible: Math.round(calc.intangible),
        'Opportunity cost avoided': Math.round(calc.opportunity),
        Total: Math.round(calc.total),
      },
      answers: {
        'Annual revenue': money(calc.rev),
        Country: calc.ctr[0],
        Industry: calc.ind.name,
        Objectives: objectives.join(', '),
      },
    }
  }, [calc, objectives])

  function submitForm() {
    const rev = Number(revenue.replace(/[^0-9]/g, ''))
    if (!rev || rev < 1e6) {
      setError(c.form.needRevenue)
      return
    }
    if (!objectives.length) {
      setError(c.form.needObjective)
      return
    }
    setError('')
    setPhase('teaser')
  }

  // ---- the form ----------------------------------------------------------
  if (phase === 'form') {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="grid gap-6 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-semibold text-foreground">{c.form.revenue}</span>
            <input
              type="text"
              inputMode="numeric"
              value={revenue}
              /* Grouped as it is typed. A nine-digit revenue is unreadable
                 without separators and mistyping one order of magnitude is the
                 easiest way to make this tool look silly. */
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '')
                setRevenue(raw ? Number(raw).toLocaleString('en-US') : '')
              }}
              placeholder={c.form.revenuePlaceholder}
              className="mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground focus:border-foreground focus:outline-none"
            />
          </label>

          <Picker
            label={c.form.country}
            value={countryIndex}
            options={countryOptions.map((o) => ({ value: o.i, name: o.name }))}
            onChange={setCountryIndex}
          />

          <div className="sm:col-span-2">
            <Picker
              label={c.form.industry}
              value={industryKey}
              options={industryOptions.map((o) => ({ value: o.key, name: o.name }))}
              onChange={setIndustryKey}
            />
          </div>

          <div className="sm:col-span-2">
            <span className="text-sm font-semibold text-foreground">{c.form.objective}</span>
            <span className="ml-2 text-sm text-muted-foreground">({c.form.objectiveHint})</span>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.keys(ROI_OBJECTIVES).map((o) => {
                const on = objectives.includes(o)
                return (
                  <button
                    key={o}
                    type="button"
                    onClick={() =>
                      setObjectives((xs) => (on ? xs.filter((x) => x !== o) : [...xs, o]))
                    }
                    className={
                      'rounded-full border px-4 py-2 text-sm font-medium transition-colors [transition-duration:var(--transition-base)] '
                      + (on
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border text-muted-foreground hover:border-foreground/40')
                    }
                  >
                    {c.objectives[o]?.label ?? o}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {error && <p className="mt-6 text-sm font-medium text-destructive">{error}</p>}

        <div className="mt-8">
          <button className="btn-primary" onClick={submitForm}>{c.form.submit}</button>
          <p className="mt-3 text-sm text-muted-foreground">{c.form.privacy}</p>
        </div>
      </div>
    )
  }

  if (!calc) return null

  // ---- the free half -----------------------------------------------------
  if (phase === 'teaser') {
    const { ind, ctr, itBudget, gt, tangible, valueRatio } = calc
    return (
      // Masked in session replay: every figure below is computed from the
      // revenue the reader typed, so showing them in a recording discloses it
      // just as plainly as the (already masked) input would.
      <div className="mx-auto max-w-3xl" data-private>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {c.teaser.eyebrow}
        </p>
        <h3 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
          {c.teaser.heading}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {fill(c.teaser.basis, {
            industry: indCopy.name,
            pct: ind.it,
            country: c.countries[ctr[0]] ?? ctr[0],
            budget: money(itBudget),
          })}
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl bg-muted p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {c.teaser.gtLabel}
            </p>
            <p className="mt-2 text-3xl font-bold text-foreground">{money(gt)}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {fill(c.teaser.gtNote, { run: ind.run })}
            </p>
          </div>
          <div className="rounded-xl bg-muted p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {c.teaser.tangibleLabel}
            </p>
            <p className="mt-2 text-3xl font-bold text-foreground">{money(tangible)}</p>
            <p className="mt-2 text-sm text-muted-foreground">{c.teaser.tangibleNote}</p>
          </div>
          <div className="rounded-xl bg-foreground p-6 text-background">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] opacity-70">
              {c.teaser.ratioLabel}
            </p>
            <p className="mt-2 text-3xl font-bold">{valueRatio.toFixed(1)}x</p>
            <p className="mt-2 text-sm opacity-80">{c.teaser.ratioNote}</p>
          </div>
        </div>

        {/* Run / Grow / Transform, to scale. The point it makes without words is
            that most of a technology budget is not an investment at all. The
            per-segment labels were dropped 2026-08-31: the narrowest segments
            render too thin to hold readable text, and the caption line below
            already spells out all three percentages legibly. */}
        <div className="mt-8 flex h-11 overflow-hidden rounded-lg text-xs font-semibold">
          <div className="flex items-center justify-center bg-foreground text-background" style={{ width: `${ind.run}%` }} />
          <div className="flex items-center justify-center bg-primary text-primary-foreground" style={{ width: `${ind.grow}%` }} />
          <div className="flex items-center justify-center bg-muted-foreground text-background" style={{ width: `${ind.tr}%` }} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {`${c.teaser.run} ${ind.run}% · ${c.teaser.grow} ${ind.grow}% · ${c.teaser.transform} ${ind.tr}%`}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button className="btn-primary" onClick={() => setPhase('gate')}>
            {c.teaser.unlock}
          </button>
          <button className="btn-secondary" onClick={() => setPhase('form')}>
            {c.report.restart}
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'gate') {
    return (
      <LeadGate
        tool="roi-calculator"
        result={result}
        openedAt={openedAt}
        onDone={(state) => {
          setSaved(state)
          setPhase('report')
          progress.forget()
        }}
      />
    )
  }

  // ---- the report --------------------------------------------------------
  const { tangible, intangible, opportunity, total, gt, digiRoi, valueRatio, costShare } = calc
  const rows: [label: string, value: number][] = [
    [c.report.tangible, tangible],
    [c.report.intangible, intangible],
    [c.report.opportunity, opportunity],
    [c.report.total, total],
  ]

  return (
    // Masked in session replay for the same reason as the teaser above.
    <div className="mx-auto max-w-3xl" data-private>
      {saved.status === 'failed' && (
        <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground">
          {t.tools.ui.notSaved}
        </p>
      )}

      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {c.report.eyebrow}
      </p>
      <h3 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
        {c.report.heading}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {fill(c.report.assumptions, {
          multiple: ROI_CFG.VALUE_MULTIPLE.toFixed(1),
          budget: money(gt),
          objectives: objectiveLabels.join(', '),
        })}
      </p>

      {/* A ledger, not four floating numbers: label, bar, amount on one ruled
          row, with the total set apart at the foot.

          The bars were invisible until 2026-08-31 — they were painted
          `bg-brand-indigo`, which is an Impact Platform token that this site's
          tailwind.config.ts does not define, so the fill had no background at
          all and the row read as a long blank gap between the label and the
          money. `bg-primary` is the site's own blue. */}
      <div className="mt-8 border-y border-border">
        {rows.map(([label, value], i) => {
          const isTotal = i === rows.length - 1
          return (
            <div
              key={label}
              className={
                'grid items-center gap-4 py-3 sm:grid-cols-[minmax(0,200px)_1fr_110px] '
                + (isTotal ? 'border-t border-border' : 'border-t border-border/60 first:border-t-0')
              }
            >
              <span
                className={
                  'text-sm '
                  + (isTotal ? 'font-bold text-foreground' : 'font-semibold text-muted-foreground')
                }
              >
                {label}
              </span>
              <div className="h-6 overflow-hidden rounded-md bg-muted">
                <div
                  className={'h-full rounded-md ' + (isTotal ? 'bg-ink' : 'bg-primary')}
                  /* Floored at 6% so the smallest bar is still a bar. */
                  style={{ width: `${Math.max(6, (value / total) * 100)}%` }}
                />
              </div>
              <span
                className={
                  'font-bold tabular-nums text-foreground sm:text-right '
                  + (isTotal ? 'text-lg' : 'text-base')
                }
              >
                {money(value)}
              </span>
            </div>
          )
        })}
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <div className="rounded-xl border border-border p-6">
          <h4 className="text-base font-semibold text-foreground">{c.report.tangible}</h4>
          <span className="mt-2 inline-block rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-foreground">
            {indCopy.kpi}
          </span>
          <p className="mt-3 text-sm text-muted-foreground">{indCopy.kdesc}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            {fill(c.report.tangibleBody, {
              tangible: money(tangible),
              objectives: objectives
                .map((o) => c.objectives[o]?.note ?? ROI_OBJECTIVES[o].note)
                .slice(0, 2)
                .join(` ${c.report.and} `),
              kpi: indCopy.kpi,
            })}
          </p>
        </div>

        <div className="rounded-xl border border-border p-6">
          <h4 className="text-base font-semibold text-foreground">{c.report.intangible}</h4>
          {c.report.intangibleBody.map((para) => {
            const [lead, ...rest] = para.split('**').filter(Boolean)
            return (
              <p key={para} className="mt-3 text-sm text-muted-foreground">
                <b className="text-foreground">{lead}</b>
                {rest.join('')}
              </p>
            )
          })}
          <p className="mt-3 text-xs text-muted-foreground">{c.report.intangibleAlso}</p>
        </div>

        <div className="rounded-xl border border-border p-6">
          <h4 className="text-base font-semibold text-foreground">{c.report.opportunity}</h4>
          <p className="mt-3 text-sm text-muted-foreground">{c.report.opportunityBody}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            {fill(c.report.opportunityEstimate, { amount: money(opportunity) })}
          </p>
        </div>
      </div>

      <div className="mt-10 grid gap-8 rounded-2xl bg-foreground p-8 text-background md:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] opacity-70">
            {c.report.digiRoiLabel}
          </p>
          <p className="mt-2 text-5xl font-bold leading-none">
            {digiRoi >= 100 ? Math.round(digiRoi) : digiRoi.toFixed(1)}x
          </p>
          <p className="mt-3 text-sm opacity-80">
            {fill(c.report.digiRoiNote, { share: Math.max(0.1, costShare).toFixed(1) })}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] opacity-70">
            {c.report.ratioLabel}
          </p>
          <p className="mt-2 text-5xl font-bold leading-none">{valueRatio.toFixed(1)}x</p>
          <p className="mt-3 text-sm opacity-80">{c.report.ratioNote}</p>
        </div>
      </div>

      <div className="mt-10 rounded-2xl bg-muted p-8 text-center">
        <h4 className="text-xl font-bold text-foreground">{c.report.ctaHeading}</h4>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">{c.report.ctaBody}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {/* `#contact` rather than the Contact Us page: the form is at the
              foot of this very page, so the button scrolls to it. */}
          <a className="btn-primary" href="#contact">{c.report.ctaLabel}</a>
          <button className="btn-secondary" onClick={() => setPhase('form')}>
            {c.report.restart}
          </button>
        </div>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">{c.report.disclaimer}</p>
    </div>
  )
}
